import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiShoppingCart, FiHeart, FiTruck, FiShield, FiRefreshCw,
  FiStar, FiZoomIn, FiShare2, FiGift, FiRotateCcw, FiCreditCard, FiEye
} from 'react-icons/fi';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import useProductStore from '../store/useProductStore';
import useCartStore from '../store/useCartStore';
import useAuthStore from '../store/useAuthStore';
import useWishlistStore from '../store/useWishlistStore';
import { showImageViewer } from '../components/ImageViewer';
import axios from '../utils/axios';
import { getProductImage } from '../utils/imageHelpers';
import ProductCard from '../components/ProductCard';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/formatPrice';

const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const { product, loading, getProductBySlug } = useProductStore();
  const { addItem } = useCartStore();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlistStore();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedRingSize, setSelectedRingSize] = useState('');
  const isWishlisted = isInWishlist(product?._id);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [reviews, setReviews] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const { user, isAuthenticated } = useAuthStore();

  useEffect(() => {
    getProductBySlug(slug);
  }, [slug]);

  useEffect(() => {
    if (product?._id) {
      fetchReviews();
      fetchSimilarProducts();
      // Reset selections
      setQuantity(1);
      setSelectedRingSize('');
      setSelectedImage(0);
      window.scrollTo(0, 0);
    }
  }, [product]);

  const fetchSimilarProducts = async () => {
    try {
      if (!product.category) return;
      const categoryId = typeof product.category === 'object' ? product.category._id : product.category;
      const { data } = await axios.get(`/products?category=${categoryId}&limit=4`);
      const similar = (data.data || []).filter(p => p._id !== product._id).slice(0, 4);
      setSimilarProducts(similar);
    } catch (error) {
      console.error('Benzer ürünler yüklenemedi');
    }
  };

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`/reviews/product/${product._id}`);
      setReviews(data.data || []);
    } catch (error) {
      console.error('Yorumlar yüklenemedi');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error(t('product.reviews.loginRequired'));
      return;
    }
    try {
      await axios.post('/reviews', {
        product: product._id,
        ...reviewForm
      });
      toast.success(t('product.reviews.success'));
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
      fetchReviews();
    } catch (error) {
      console.error('Review submit error:', error);
      toast.error(error.response?.data?.message || t('product.reviews.error'));
    }
  };

  const renderStars = (rating, size = 'text-base') => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className={`${size} text-yellow-400`} />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className={`${size} text-yellow-400`} />);
    }
    const remaining = 5 - stars.length;
    for (let i = 0; i < remaining; i++) {
      stars.push(<FaRegStar key={`empty-${i}`} className={`${size} text-yellow-400`} />);
    }
    return stars;
  };

  const handleAddToCart = async () => {
    if (product.hasRingSize && !selectedRingSize) {
      toast.error(t('product.ringSize.select'));
      return;
    }
    try {
      await addItem(product._id, quantity, { ringSize: selectedRingSize });
      toast.success(t('product.messages.addToCartSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('product.messages.addToCartError'));
    }
  };

  const handleWishlist = async () => {
    try {
      if (isWishlisted) {
        await removeFromWishlist(product._id);
      } else {
        await addToWishlist(product._id);
      }
    } catch (error) {
      toast.error(t('product.messages.error'));
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `${product.name} - ${formatPrice(price)} ₺`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        toast.success('Link kopyalandı!');
        setShowShareMenu(false);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center gap-4 animate-fadeIn">
        <div className="font-serif text-3xl font-light tracking-[0.3em] text-gray-900 animate-pulse">MASHOW</div>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-gray-600 font-light tracking-wide mb-8">{t('product.notFound')}</p>
        <Link to="/products" className="btn-primary">
          {t('product.backToCollection')}
        </Link>
      </div>
    );
  }

  const getBadgeDescription = (badgeKey) => {
    const isTr = i18n.language?.startsWith('tr');
    const descMap = {
      freeShipping: isTr 
        ? "500 TL ve üzeri siparişlerinizde tüm Türkiye'ye ücretsiz kargo fırsatı." 
        : "Free shipping across all of Turkey on orders of 500 TL and above.",
      insurance: isTr
        ? "Siparişiniz teslimata kadar Mashow güvencesiyle %100 sigortalıdır."
        : "Your order is 100% insured under Mashow guarantee until delivery.",
      return: isTr
        ? "Satın aldığınız ürünleri 14 gün içerisinde kolayca iade edebilirsiniz."
        : "You can easily return your purchased items within 14 days.",
      installment: isTr
        ? "Tüm kredi kartlarına vade farksız cazip taksit seçenekleri."
        : "Attractive installment options with no interest on all credit cards.",
      giftNote: isTr
        ? "Özel tasarım hediye kutusu, hediye çantası ve şık hediye notu kartı."
        : "Custom gift box, branded gift bag, and an elegant gift note card."
    };
    return descMap[badgeKey] || '';
  };

  const price = product.salePrice || product.price || 0;
  const hasDiscount = product.salePrice > 0 && product.salePrice < product.price;

  return (
    <div className="min-h-screen bg-white text-[#4a4a4a]">
      {/* Breadcrumb */}
      <div className="bg-white py-4 px-6 lg:px-12 border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-wider text-gray-500 font-medium">
            <Link to="/" className="hover:text-primary transition">{t('breadcrumbs.home')}</Link>
            <span>›</span>
            <Link to="/products" className="hover:text-primary transition">{t('breadcrumbs.collection')}</Link>
            <span>›</span>
            <span className="text-gray-900">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-6 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          
          {/* LEFT COLUMN: IMAGES (7/12) */}
          <div className="lg:col-span-7 flex flex-col gap-4 lg:gap-6 lg:sticky lg:top-28 z-10 self-start h-fit">
            {/* Main Image Wrapper */}
            <div className="relative w-full bg-white overflow-hidden flex items-center justify-center aspect-[4/5] md:aspect-square lg:aspect-auto lg:h-[650px] group">
              {product.images && product.images.length > 0 ? (
                <>
                  <div
                    className={`w-full h-full flex items-center justify-center ${window.innerWidth >= 1024 ? (isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in') : ''}`}
                    onClick={() => {
                      if (window.innerWidth >= 1024) setIsZoomed(!isZoomed);
                    }}
                    onMouseMove={(e) => {
                      if (window.innerWidth >= 1024 && isZoomed) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                        const y = ((e.clientY - rect.top) / rect.height) * 100;
                        setZoomPosition({ x, y });
                      }
                    }}
                  >
                    <img
                      src={getProductImage(product.images, selectedImage)}
                      alt={product.name}
                      className={`max-w-full max-h-full object-contain p-4 lg:p-12 transition-transform duration-300 ${isZoomed ? 'scale-150' : 'scale-100'}`}
                      style={isZoomed ? { transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%` } : {}}
                    />
                  </div>

                  {/* Zoom Controls */}
                  <div className="hidden lg:flex absolute bottom-6 right-6 z-10 flex-col gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); setIsZoomed(true); }}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-lg text-gray-600 hover:text-primary transition"
                    >
                      <span className="text-xl font-bold">+</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-lg text-gray-600 hover:text-primary transition"
                    >
                      <span className="text-xl font-bold">−</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">{t('product.noImage')}</div>
              )}
            </div>

            {/* Thumbnail Reel (Horizontal row below main image) */}
            {product.images && product.images.length > 1 && (
              <div className="flex gap-3 lg:gap-4 overflow-x-auto scrollbar-hide py-2 px-1">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 aspect-square bg-white overflow-hidden transition-all duration-300 ${
                      selectedImage === index 
                        ? 'ring-2 ring-[var(--primary-color)] ring-offset-1 scale-[1.02]' 
                        : 'opacity-60 hover:opacity-100 hover:scale-[1.02]'
                    }`}
                  >
                    <img
                      src={getProductImage(product.images, index)}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: INFO (5/12) */}
          <div className="lg:col-span-5 flex flex-col pt-2 lg:pt-0">
            
            {/* Category / Brand Tag */}
            <div className="mb-3 lg:mb-4">
              <span className="text-primary font-bold tracking-[0.25em] text-[10px] lg:text-[11px] uppercase">
                {product.category?.name || 'MASHOW'}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] font-medium text-[#2c2c2c] mb-4 lg:mb-6 leading-[1.15]">
              {product.name}
            </h1>

            {/* Stats Row: Rating, Views, Stock */}
            <div className="flex flex-wrap items-center gap-4 mb-8 text-xs font-semibold tracking-wider uppercase text-gray-500">
              <button 
                onClick={() => {
                  document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 border-r border-gray-200 pr-4 hover:opacity-80 transition-opacity cursor-pointer text-left"
              >
                <div className="flex text-gray-300 text-sm">
                  {renderStars(product.rating?.average || 0)}
                </div>
                <span className="text-gray-900">{(product.rating?.average || 0).toFixed(1)}</span>
                <span className="text-gray-400">({product.rating?.count || 0} DEĞERLENDİRME)</span>
              </button>
              
              <div className="flex items-center gap-2 border-r border-gray-200 pr-4 text-gray-400">
                <FiEye className="text-sm" />
                <span>{product.viewCount || 0} GÖRÜNTÜLENME</span>
              </div>

              <div>
                {product.stock > 0 ? (
                  <span className="bg-primary text-white px-3 py-1 rounded-sm tracking-widest text-[10px]">
                    STOKTA
                  </span>
                ) : (
                  <span className="bg-gray-200 text-gray-600 px-3 py-1 rounded-sm tracking-widest text-[10px]">
                    TÜKENDİ
                  </span>
                )}
              </div>
            </div>

            {/* Separator */}
            <div className="h-px bg-gray-200 w-full mb-8" />

            {/* Description */}
            <div className="mb-10">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-900 mb-4">
                AÇIKLAMA
              </h3>
              <div className="prose prose-sm max-w-none text-gray-600 font-light leading-relaxed">
                <p>{product.description}</p>
              </div>
            </div>

            {/* Product Details Table (Sleek) */}
            <div className="mb-10 bg-gray-50 p-6 rounded-lg">
               <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-900 mb-4">
                ÖZELLİKLER
              </h3>
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                {product.modelNumber && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.modelCode', 'Model Kodu')}</span>
                    <span className="font-medium">{product.modelNumber}</span>
                  </div>
                )}
                {product.productCode && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.productCode', 'Ürün Kodu')}</span>
                    <span className="font-medium">{product.productCode}</span>
                  </div>
                )}
                {product.gender && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('products.gender', 'Cinsiyet')}</span>
                    <span className="font-medium capitalize">{product.gender}</span>
                  </div>
                )}
                {product.material && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.material', 'Materyal')}</span>
                    <span className="font-medium capitalize">
                      {product.material.toLowerCase() === 'rose' ? 'Rose Gold' : product.material}
                    </span>
                  </div>
                )}
                {product.carat && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.carat', 'Ayar')}</span>
                    <span className="font-medium">
                      {typeof product.carat === 'string'
                        ? (product.carat.toLowerCase().includes('ayar') || product.carat.toLowerCase().includes('k')
                          ? product.carat 
                          : `${product.carat} Ayar`)
                        : `${product.carat} Ayar`}
                    </span>
                  </div>
                )}
                {product.gram && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.weight', 'Ürün Ağırlığı')}</span>
                    <span className="font-medium">{product.gram} gr</span>
                  </div>
                )}
                {product.size && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.specs.size', 'Boyut / Boy (cm)')}</span>
                    <span className="font-medium">{product.size}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500 block text-xs uppercase mb-1">{t('products.hasStone', 'Taş Durumu')}</span>
                  <span className="font-medium">{product.hasStone ? 'Taşlı' : 'Taşsız'}</span>
                </div>
                {product.hasStone && product.stoneType && product.stoneType.toLowerCase() !== 'taşsız' && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('products.stoneType', 'Taş Türü')}</span>
                    <span className="font-medium capitalize">{product.stoneType}</span>
                  </div>
                )}
                {product.diamondCarat && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.diamondCarat', 'Pırlanta Karat')}</span>
                    <span className="font-medium">{product.diamondCarat} ct</span>
                  </div>
                )}
                {product.stoneColor && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.stoneColor', 'Taş Rengi')}</span>
                    <span className="font-medium uppercase">{product.stoneColor}</span>
                  </div>
                )}
                {product.certificate && (
                  <div>
                    <span className="text-gray-500 block text-xs uppercase mb-1">{t('product.details.certificate', 'Sertifika')}</span>
                    <span className="font-medium uppercase">{product.certificate}</span>
                  </div>
                )}

              </div>
            </div>

            {/* Selectors */}
            <div className="space-y-6 mt-auto">
              {/* Ring Size Selector */}
              {product.hasRingSize && product.ringSizes?.length > 0 && (
                <div>
                  <div className="flex justify-between mb-3">
                    <label className="text-xs font-bold uppercase tracking-widest text-gray-900">{t('product.ringSize.label')}</label>
                    <Link to="/ring-size-guide" className="text-xs text-primary hover:text-primary-800 transition-colors">
                      {t('product.ringSize.unknown')}
                    </Link>
                  </div>
                  <select
                    className="w-full border-b-2 border-gray-200 py-3 text-sm focus:border-primary outline-none bg-transparent transition-colors"
                    value={selectedRingSize}
                    onChange={(e) => setSelectedRingSize(e.target.value)}
                  >
                    <option value="">{t('product.ringSize.select')}</option>
                    {product.ringSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Price & Action Button Area */}
              <div className="pt-4 flex flex-col gap-5 lg:gap-6">
                {/* Price */}
                <div className="flex items-baseline gap-4 bg-gray-50/50 p-4 lg:p-6 rounded-lg border border-gray-100">
                  <span className="text-3xl lg:text-4xl font-serif font-medium text-gray-900 tracking-tight">
                    {formatPrice(price)} ₺
                  </span>
                  {hasDiscount && (
                    <span className="text-lg lg:text-xl text-gray-400 line-through font-light">
                      {formatPrice(product.price)} ₺
                    </span>
                  )}
                </div>

                {/* Add To Cart */}
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  className="w-full bg-primary text-white py-4 lg:py-5 text-sm tracking-[0.2em] uppercase font-bold flex items-center justify-center gap-3 hover:bg-primary-700 transform active:scale-[0.98] transition-all duration-300 rounded-sm shadow-xl shadow-primary/20 hover:shadow-primary/30"
                >
                  {product.stock === 0 ? t('common.outOfStock') : t('common.addToCart')}
                </button>

                {/* Links: Favori, Paylaş */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-[11px] font-bold tracking-widest text-gray-500 uppercase">
                  <div className="flex gap-6">
                    <button 
                      onClick={handleWishlist}
                      className={`flex items-center gap-2 transition-colors ${isWishlisted ? 'text-red-500' : 'hover:text-red-500'}`}
                    >
                      <FiHeart className={`text-sm ${isWishlisted ? 'fill-current' : ''}`} />
                      FAVORİLERE EKLE
                    </button>
                  </div>

                  <div className="relative">
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="flex items-center gap-2 hover:text-primary transition-colors"
                    >
                      <FiShare2 className="text-sm" />
                      PAYLAŞ
                    </button>

                    {showShareMenu && (
                      <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded shadow-xl border border-gray-100 py-2 z-50 normal-case tracking-normal">
                        <button onClick={() => handleShare('facebook')} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3">
                          <span className="text-blue-600">📘</span> Facebook
                        </button>
                        <button onClick={() => handleShare('whatsapp')} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3">
                          <span className="text-green-600">💬</span> WhatsApp
                        </button>
                        <button onClick={() => handleShare('twitter')} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3">
                          <span className="text-blue-400">🐦</span> Twitter
                        </button>
                        <div className="border-t border-gray-100 my-1"></div>
                        <button onClick={() => handleShare('copy')} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3">
                          <span>📋</span> Link Kopyala
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Icons / Benefits */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mt-24 pt-12 border-t border-gray-100">
          <Link to="/shipping" className="flex flex-col items-center text-center gap-3 group hover:scale-[1.02] transition-transform duration-300">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <FiTruck className="text-2xl" />
            </div>
            <span className="text-xs font-bold tracking-widest uppercase text-gray-800">{t('footer.badges.freeShipping')}</span>
            <p className="text-[11px] text-gray-500 font-light max-w-[200px] leading-relaxed">
              {getBadgeDescription('freeShipping')}
            </p>
          </Link>
          <Link to="/shipping" className="flex flex-col items-center text-center gap-3 group hover:scale-[1.02] transition-transform duration-300">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <FiShield className="text-2xl" />
            </div>
            <span className="text-xs font-bold tracking-widest uppercase text-gray-800">{t('footer.badges.insurance')}</span>
            <p className="text-[11px] text-gray-500 font-light max-w-[200px] leading-relaxed">
              {getBadgeDescription('insurance')}
            </p>
          </Link>
          <Link to="/returns" className="flex flex-col items-center text-center gap-3 group hover:scale-[1.02] transition-transform duration-300">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <FiRotateCcw className="text-2xl" />
            </div>
            <span className="text-xs font-bold tracking-widest uppercase text-gray-800">{t('footer.badges.return')}</span>
            <p className="text-[11px] text-gray-500 font-light max-w-[200px] leading-relaxed">
              {getBadgeDescription('return')}
            </p>
          </Link>
          <Link to="/payment-options" className="flex flex-col items-center text-center gap-3 group hover:scale-[1.02] transition-transform duration-300">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <FiCreditCard className="text-2xl" />
            </div>
            <span className="text-xs font-bold tracking-widest uppercase text-gray-800">{t('footer.badges.installment')}</span>
            <p className="text-[11px] text-gray-500 font-light max-w-[200px] leading-relaxed">
              {getBadgeDescription('installment')}
            </p>
          </Link>
          <Link to="/faq" className="flex flex-col items-center text-center gap-3 group hover:scale-[1.02] transition-transform duration-300">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <FiGift className="text-2xl" />
            </div>
            <span className="text-xs font-bold tracking-widest uppercase text-gray-800">{t('footer.badges.giftNote')}</span>
            <p className="text-[11px] text-gray-500 font-light max-w-[200px] leading-relaxed">
              {getBadgeDescription('giftNote')}
            </p>
          </Link>
        </div>

        {/* Reviews Section */}
        <div id="reviews-section" className="mt-24 pt-12 border-t border-gray-100">
          <div className="mb-12">
            <h2 className="font-serif text-2xl text-gray-900 mb-8">{t('product.reviews.title')}</h2>
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <p className="text-gray-500 mb-4">{reviews.length > 0 ? reviews.length : (product.rating?.count || 0)} {t('product.reviews.count')}</p>
              <button
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="btn-outline px-8"
              >
                {t('product.reviews.writeReview')}
              </button>

              {showReviewForm && (
                <div className="max-w-xl mx-auto mt-8 text-left bg-white p-6 rounded shadow-sm">
                  <form onSubmit={handleReviewSubmit}>
                    <div className="mb-4">
                      <label className="block text-sm mb-1">{t('product.reviews.form.rating')}</label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(s => (
                          <FaStar
                            key={s}
                            className={`cursor-pointer text-xl ${s <= reviewForm.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                            onClick={() => setReviewForm({ ...reviewForm, rating: s })}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm mb-1">{t('product.reviews.form.title')}</label>
                      <input
                        className="input-field border w-full p-2"
                        placeholder={t('product.reviews.form.titlePlaceholder')}
                        value={reviewForm.title}
                        onChange={e => setReviewForm({ ...reviewForm, title: e.target.value })}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm mb-1">{t('product.reviews.form.comment')}</label>
                      <textarea
                        className="input-field border w-full p-2"
                        rows="3"
                        placeholder={t('product.reviews.form.commentPlaceholder')}
                        value={reviewForm.comment}
                        onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                      ></textarea>
                    </div>
                    <button type="submit" className="bg-primary text-white py-2 px-4 rounded w-full">{t('product.reviews.form.submit')}</button>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-8 max-w-4xl mx-auto">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review._id} className="border-b border-gray-100 pb-8 last:border-0">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-xl font-medium text-gray-400">
                        {review.user?.firstName?.[0] || 'U'}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {review.user?.firstName} {review.user?.lastName}
                          {review.isVerifiedPurchase && (
                            <span className="ml-2 text-xs text-green-600 font-normal bg-green-50 px-2 py-0.5 rounded-full">
                              {t('product.reviews.verifiedBuyer')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex text-yellow-400 text-sm">
                            {renderStars(review.rating)}
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(review.createdAt).toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {review.title && (
                    <h4 className="font-medium text-gray-900 mb-2">{review.title}</h4>
                  )}

                  <p className="text-gray-600 font-light leading-relaxed">
                    {review.comment}
                  </p>

                  {review.adminResponse && (
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-100 ml-8">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {t('product.reviews.storeResponse')}
                      </div>
                      <p className="text-sm text-gray-600 font-light">
                        {review.adminResponse.message}
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8 font-light">
                {t('product.reviews.noReviews')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
